import React from 'react';
import { Curve, CurveModifier } from 'curves';
import { Easing } from 'eaz';
import './CodeEditor.scss';
import { ModifierCurveType } from '../ModifierCurveType';
import { getDefinition, getModifierArgs } from '../ModifierEditor/ModifierEditor';

interface CodeEditorProps<T> {
  curve: React.RefObject<Curve<T>>;
  modifierType?: ModifierCurveType;
}

type KeyframeConfig = {
  className: string;
  valueType: string;
};

const keyframeConfigs: { [key in ModifierCurveType]: KeyframeConfig } = {
  Number: { className: 'NumberKeyframe', valueType: 'number' },
  Bezier: { className: 'BezierKeyframe', valueType: 'number' },
  RGB: { className: 'RGBColorKeyframe', valueType: 'RGBColor' },
  HSV: { className: 'HSVColorKeyframe', valueType: 'HSVColor' },
  List: { className: 'ListKeyframe', valueType: 'number[]' },
  Boolean: { className: 'BooleanKeyframe', valueType: 'boolean' },
  Vector3: { className: 'Vector3Keyframe', valueType: 'Vector3' },
  String: { className: 'StringKeyframe', valueType: 'string' },
  Object: { className: 'ObjectKeyframe', valueType: 'object' },
};

const easingOptions = {
  linear: Easing.linear,
  quadratic: Easing.quadratic,
  cubic: Easing.cubic,
  quartic: Easing.quartic,
  quintic: Easing.quintic,
  exponential: Easing.exponential(),
  sinusoidal: Easing.sinusoidal,
  halfSine: Easing.halfSine,
  circular: Easing.circular,
  elastic: Easing.elastic,
  back: Easing.back(),
  stepped: Easing.stepped(),
  wiggle: Easing.wiggle(),
  noise: Easing.noise(),
};

const modifierNamespaceByType: { [key in ModifierCurveType]: string } = {
  Number: 'Number',
  Bezier: 'Number',
  RGB: 'RGB',
  HSV: 'HSV',
  List: 'List',
  Boolean: 'Boolean',
  Vector3: 'Vector3',
  String: 'String',
  Object: 'Object',
};

function indent(value: string, spaces = 2) {
  return value.split('\n').map((line) => `${' '.repeat(spaces)}${line}`).join('\n');
}

function serializeValue(value: any): string {
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') return Number.isFinite(value) ? value.toString() : `Number.${value > 0 ? 'MAX_VALUE' : 'MIN_VALUE'}`;
  if (typeof value === 'boolean') return value.toString();
  return JSON.stringify(value, null, 2);
}

function easingExpression(easing: any) {
  const entry = Object.keys(easingOptions).find((key) => (
    easingOptions[key] === easing
    || easingOptions[key].toString() === easing.toString()
  ));
  if (entry === undefined) return 'Easing.cubic';
  if (['exponential', 'back', 'stepped', 'wiggle', 'noise'].includes(entry)) return `Easing.${entry}()`;
  return `Easing.${entry}`;
}

function keyframeExpression(key: any, keyframeClass: string) {
  if (keyframeClass === 'BezierKeyframe') {
    if (key.automaticHandles) {
      return `BezierKeyframe.automatic(${serializeValue(key.time)}, ${serializeValue(key.value)}, ${serializeValue(key.defaultMagnitude)})`;
    }

    return [
      `new BezierKeyframe(`,
      indent(`${serializeValue(key.time)},`),
      indent(`${serializeValue(key.value)},`),
      indent(`new BezierHandle(${serializeValue(key.inHandle.angle)}, ${serializeValue(key.inHandle.magnitude)}),`),
      indent(`new BezierHandle(${serializeValue(key.outHandle.angle)}, ${serializeValue(key.outHandle.magnitude)}),`),
      indent(`${serializeValue(key.defaultMagnitude)},`),
      ')',
    ].join('\n');
  }

  const args = [serializeValue(key.time), serializeValue(key.value)];
  const inEasing = easingExpression(key.inEasing);
  const outEasing = easingExpression(key.outEasing);
  if (inEasing !== 'Easing.cubic' || outEasing !== 'Easing.cubic') {
    args.push(inEasing);
  }
  if (outEasing !== inEasing) {
    args.push(outEasing);
  }
  return `new ${keyframeClass}(${args.join(', ')})`;
}

function modifierArgumentExpression(arg: any, fieldName: string) {
  if (fieldName === 'blend') {
    const blendName = ['Replace', 'Add', 'Subtract', 'Multiply', 'MultiplyPositive'][arg] ?? 'Replace';
    return `SineModifierBlendMode.${blendName}`;
  }
  return serializeValue(arg);
}

function modifierExpression(type: ModifierCurveType, modifier: CurveModifier<any>) {
  const definition = getDefinition(type, modifier);
  if (definition === undefined) return undefined;

  const args = getModifierArgs(modifier, definition);
  const namespace = definition.label === 'Cycles' ? 'Any' : modifierNamespaceByType[type];
  const renderedArgs = args.map((arg, index) => modifierArgumentExpression(arg, definition.fields[index].name));

  return `curve.addModifier(new Modifiers.${namespace}.${definition.label}(${renderedArgs.join(', ')}));`;
}

function CodeEditor<T>(props: CodeEditorProps<T>): React.ReactElement {
  if (!props.curve.current) return <div>No curve found!</div>;
  if (props.modifierType === undefined) return <div>No modifier type configured for this demo.</div>;

  const config = keyframeConfigs[props.modifierType];
  const imports = new Set(['Curve', config.className]);
  if (['RGB', 'HSV', 'Vector3'].includes(props.modifierType)) imports.add(config.valueType);
  if (props.curve.current.modifiers.length > 0) imports.add('Modifiers');

  const keyframes = props.curve.current.keys.map((key) => keyframeExpression(key, config.className));
  const modifiers = props.curve.current.modifiers
    .map((modifier) => modifierExpression(props.modifierType as ModifierCurveType, modifier))
    .filter((line): line is string => line !== undefined);

  const usesEasing = keyframes.some((line) => line.includes('Easing.'));
  const usesSineBlendMode = modifiers.some((line) => line.includes('SineModifierBlendMode.'));
  if (usesSineBlendMode) imports.add('SineModifierBlendMode');
  if (props.modifierType === 'Bezier') imports.add('BezierHandle');

  const lines = [
    `import { ${Array.from(imports).join(', ')} } from 'curves';`,
    ...(usesEasing ? [`import { Easing } from 'eaz';`] : []),
    '',
    `const curve: Curve<${config.valueType}> = new Curve<${config.valueType}>([`,
    keyframes.map((line) => indent(`${line},`)).join('\n'),
    ']);',
    ...(modifiers.length > 0 ? ['', ...modifiers] : []),
  ];

  return (
    <pre className="code-editor">
      <code>{lines.join('\n')}</code>
    </pre>
  );
}

export default CodeEditor;
